import { $api } from "@/shared/api/api";
import { IApp } from "@/shared/types/apps";
import type { Game, GameUrls, SWFHeader, UrlGame } from "@/shared/types/flash";
import AppsService from "./AppsService";
import { electronAPI } from "./electronIPC";

export class GameManager {
  // private static readonly CACHE_DIR = "games";
  private static readonly TEMP_DIR = "flash-game-launcher";

  /**
   * Получить путь к кешу игр
   * Зарезервировано для будущего использования (может использоваться для долгосрочного кеширования)
   */
  // private static async getCacheDir(): Promise<string> {
  //   const cachePath = await electronAPI.getCachePath();
  //   // Определяем разделитель пути по содержимому пути
  //   const separator = cachePath.includes("\\") ? "\\" : "/";
  //   return `${cachePath}${separator}${this.CACHE_DIR}`;
  // }

  /**
   * Получить путь к временным файлам
   */
  private static async getTempDir(): Promise<string> {
    const tempPath = await electronAPI.getTempPath();
    const separator = tempPath.includes("\\") ? "\\" : "/";
    return `${tempPath}${separator}${this.TEMP_DIR}`;
  }

  /**
   * Преобразовать IApp в Game формат
   */
  private static convertIAppToGame(app: IApp): Game {
    return {
      appId: app.id.toString(),
      title: app.title,
      type: app.type || (app.embedded_url ? "url" : "local"),
      fileName: app.fileName,
      url: app.embedded_url,
    };
  }

  /**
   * Получить информацию об игре с сервера
   */
  static async getGameInfo(appId: string): Promise<Game> {
    try {
      const app = await AppsService.getAppById(appId);
      return this.convertIAppToGame(app);
    } catch (error) {
      throw new Error(`Failed to get game info: ${error}`);
    }
  }

  /**
   * Скачать игру с сервера и сохранить во временный файл
   * Возвращает URLs для разных плееров или URL для iframe режима
   */
  static async downloadGame(appId: string): Promise<GameUrls | UrlGame> {
    // Получить информацию об игре
    const gameInfo = await this.getGameInfo(appId);
    const gameType = gameInfo.type || (gameInfo.url ? "url" : "local");

    // Для URL игр возвращаем URL напрямую
    if (gameType === "url" && gameInfo.url) {
      console.log(
        `[GameManager] URL game detected: ${appId} -> ${gameInfo.url}`,
      );
      return {
        type: "url",
        url: gameInfo.url,
      };
    }

    // Для локальных игр - скачиваем SWF файл
    const tempDir = await this.getTempDir();
    const separator = tempDir.includes("\\") ? "\\" : "/";
    const timestamp = Date.now();
    const tempPath = `${tempDir}${separator}${appId}_${timestamp}.swf`;

    console.log(`[GameManager] Downloading SWF: ${appId}`);

    try {
      // Получаем signed_url для скачивания файла
      const { signed_url } = await AppsService.getAppUrl(appId);

      // Скачиваем файл используя $api с responseType: 'arraybuffer'
      const response = await $api.get<ArrayBuffer>(signed_url, {
        responseType: "arraybuffer",
      });

      const buffer = response.data;
      const data = new Uint8Array(buffer);

      // Сохранить во временный файл
      await electronAPI.writeFile(tempPath, data);
      console.log(`[GameManager] Saved temp SWF: ${tempPath}`);

      // Создать Data URL для Ruffle
      const base64 = this.arrayBufferToBase64(buffer);
      const dataUrl = `data:application/x-shockwave-flash;base64,${base64}`;

      // Регистрируем файл в локальном HTTP сервере и получаем URL
      const fileUrl = await electronAPI.registerSwfFile(tempPath);

      console.log(`[GameManager] Created URLs for: ${appId}`);
      console.log(`[GameManager] - Data URL size: ${dataUrl.length} chars`);
      console.log(`[GameManager] - File URL: ${fileUrl}`);
      console.log(`[GameManager] - Local path: ${tempPath}`);

      return {
        dataUrl,
        fileUrl,
        localPath: tempPath,
      };
    } catch (error) {
      // Если не удалось скачать через signed_url, пробуем использовать url напрямую
      if (gameInfo.url) {
        console.warn(
          `[GameManager] Failed to download SWF, using URL directly: ${error}`,
        );
        return {
          type: "url",
          url: gameInfo.url,
        };
      }
      throw new Error(`Failed to download game: ${error}`);
    }
  }

  /**
   * Преобразовать ArrayBuffer в base64 строку
   */
  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Проанализировать SWF header
   */
  static analyzeSWFHeader(swfData: Uint8Array): SWFHeader {
    const signature = String.fromCharCode(swfData[0], swfData[1], swfData[2]);
    const version = swfData[3];
    const fileSize = new DataView(swfData.buffer).getUint32(4, true);
    const frameRate = new DataView(swfData.buffer).getUint16(12, true) >> 8;

    return {
      signature,
      version,
      fileSize,
      frameRate,
      isCompressed: signature !== "FWS",
      isAS3: version >= 9,
      isAS2: version >= 6 && version < 9,
    };
  }

  /**
   * Очистить старые временные файлы (старше 1 часа)
   */
  static async clearOldTempFiles(): Promise<void> {
    try {
      const tempDir = await this.getTempDir();
      const exists = await electronAPI.fileExists(tempDir);

      if (!exists) {
        console.log(
          "[GameManager] Temp directory doesn't exist, nothing to clean",
        );
        return;
      }

      const files = await electronAPI.readDir(tempDir);
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;

      for (const file of files) {
        if (file.endsWith(".swf")) {
          // Извлекаем timestamp из имени файла
          const match = file.match(/_(\d+)\.swf$/);
          if (match) {
            const timestamp = parseInt(match[1], 10);
            if (now - timestamp > oneHour) {
              const separator = tempDir.includes("\\") ? "\\" : "/";
              const filePath = `${tempDir}${separator}${file}`;
              const deleted = await electronAPI.deleteFile(filePath);
              if (deleted) {
                console.log(`[GameManager] Deleted old temp file: ${file}`);
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn(`[GameManager] Failed to clear temp files: ${error}`);
    }
  }

  /**
   * Очистить кеш
   */
  static async clearCache(): Promise<void> {
    console.log("[GameManager] Clearing cache...");
    await this.clearOldTempFiles();
  }
}
